<script setup lang="ts">
import { computed } from "vue";
import { UIcon } from "#components";
import type { DatabaseSkill } from "~/typings/database";

const props = defineProps<{
  skills: DatabaseSkill[];
}>();

// Filter and get top 5 skills for each category
const categorizedSkills = computed(() => {
  const categories = ["language", "framework", "platform"] as const;

  return categories.map((category) => {
    const skillsInCategory = props.skills
      .filter((skill) => skill.categories.includes(category))
      .sort((a, b) => b.relevance - a.relevance)
      .slice(0, 5);

    return {
      category,
      skills: skillsInCategory,
    };
  });
});

const getCategoryTitle = (category: string): string => {
  const titles: Record<string, string> = {
    language: "Languages",
    framework: "Frameworks & Libraries",
    platform: "Platforms & DevOps",
  };
  return titles[category] || category;
};
</script>

<template>
  <div class="mb-8">
    <UiTitle level="3" class="text-3xl font-semibold mb-4">
      Tools I work with
    </UiTitle>
  </div>

  <UiCard
    class="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:divide-x lg:divide-primary-200 dark:lg:divide-primary-700"
  >
    <div
      v-for="categoryGroup in categorizedSkills"
      :key="categoryGroup.category"
      class="flex flex-col"
    >
      <UiTitle level="4" class="text-xl font-semibold mb-4 lg:mb-8">
        {{ getCategoryTitle(categoryGroup.category) }}
      </UiTitle>

      <div class="flex flex-col gap-3">
        <div
          v-for="skill in categoryGroup.skills"
          :key="skill.id"
          class="flex items-center gap-3"
        >
          <UIcon
            v-if="skill.isIconFont && skill.icon.startsWith('i-custom-')"
            :name="skill.icon"
            mode="svg"
            class="text-2xl text-primary-500 dark:text-primary-400"
          />
          <div
            v-else-if="skill.isIconFont"
            :class="skill.icon"
            class="text-2xl text-primary-500 dark:text-primary-400"
            aria-hidden="true"
          ></div>
          <img
            v-else
            :src="skill.icon"
            :alt="`${skill.name} icon`"
            class="w-6 h-6"
          />
          <span class="text-base font-medium text-gray-700 dark:text-gray-300">
            {{ skill.name }}
          </span>
        </div>
      </div>
    </div>
  </UiCard>
</template>
